import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedAdminRoute } from "@/components/auth/ProtectedAdminRoute";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { Loading } from "@/components/ui/loading";
import {
  ADMIN_HOME_PATH,
  INVOICE_DRAFT_EDITOR_ROUTE,
  INVOICE_EDITOR_ROUTE,
  ROUTES,
  adminSections,
} from "@/lib/navigation";
import { adminSectionElements } from "@/pages/admin/sections";
import { HomePage } from "@/pages/HomePage";
import {
  AdminLayout,
  BookingPage,
  InvoiceEditorPage,
  LoginPage,
  MyBookingsPage,
  PaymentResultPage,
  ProfilePage,
  RegisterPage,
  SetupAdminPage,
} from "@/pages/lazyPages";

// Átirányító komponens a bejelentkezés után
function AuthRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return <Loading label="Alkalmazás betöltése..." />;
  }

  // replace: a /auth-redirect csak átmenő állomás, nem maradhat a előzményekben,
  // különben a cél oldalról visszalépve azonnal újra előre dobná a felhasználót
  if (!user) {
    return <Navigate to={ROUTES.login} replace />;
  }

  if (user.isAdmin) {
    return <Navigate to={ROUTES.admin} replace />;
  }

  return <Navigate to={ROUTES.myBookings} replace />;
}

function App() {
  return (
    <ThemeProvider defaultTheme="system" storageKey="arizona-ui-theme">
      <TooltipProvider delay={300}>
        <Router>
          <AuthProvider>
            {/* `min-h-svh`, nem `min-h-screen`: a számla-szerkesztő `h-svh` magas, a `min-h-screen`
                (100vh = a NAGY nézetablak) miatt mobilon kinyitott eszköztárnál a dokumentum egy
                eszköztárnyit görgethetett, és üres sáv jelent meg a szerkesztő alatt - rövid oldalon
                a `min-h-svh` sem rosszabb, a lábléc ugyanúgy alul marad. */}
            <div className="flex min-h-svh flex-col">
              <Routes>
                <Route element={<SiteLayout />}>
                  <Route path={ROUTES.home} element={<HomePage />} />
                  <Route path={ROUTES.booking} element={<BookingPage />} />
                  <Route path={ROUTES.login} element={<LoginPage />} />
                  <Route path={ROUTES.register} element={<RegisterPage />} />
                  <Route path={ROUTES.myBookings} element={<MyBookingsPage />} />
                  <Route path={ROUTES.profile} element={<ProfilePage />} />
                  <Route
                    path="/setup-admin"
                    element={
                      <ProtectedAdminRoute>
                        <SetupAdminPage />
                      </ProtectedAdminRoute>
                    }
                  />
                  <Route path="/auth-redirect" element={<AuthRedirect />} />

                  {/* Fizetési eredmény oldalak */}
                  <Route path="/payment/success" element={<PaymentResultPage outcome="success" />} />
                  <Route path="/payment/cancel" element={<PaymentResultPage outcome="cancel" />} />
                  <Route path="/payment/error" element={<PaymentResultPage outcome="error" />} />

                  {/* Ismeretlen cím (elgépelés, régi könyvjelző): a főoldalra visszük,
                      különben – mivel a fejléc és a lábléc is route-elem – üres lapot kapna */}
                  <Route path="*" element={<Navigate to={ROUTES.home} replace />} />
                </Route>

                {/* Admin: a szekciók saját címet kapnak (/admin/bookings, ...), a fejléc ebből tudja az aktívat */}
                <Route
                  path={ROUTES.admin}
                  element={
                    <ProtectedAdminRoute>
                      <AdminLayout />
                    </ProtectedAdminRoute>
                  }
                >
                  <Route index element={<Navigate to={ADMIN_HOME_PATH} replace />} />
                  {adminSections.map((section) => (
                    <Route key={section.id} path={section.id} element={adminSectionElements[section.id]} />
                  ))}
                  <Route path="*" element={<Navigate to={ADMIN_HOME_PATH} replace />} />
                </Route>

                {/* A számla-szerkesztő teljes képernyős, az admin kereten (fejléc, lábléc) KÍVÜL -
                    a szekció útvonalai alatt él, de a pontosabb útvonal nyer az /admin/* felett */}
                <Route
                  path={INVOICE_EDITOR_ROUTE}
                  element={
                    <ProtectedAdminRoute>
                      <InvoiceEditorPage />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path={INVOICE_DRAFT_EDITOR_ROUTE}
                  element={
                    <ProtectedAdminRoute>
                      <InvoiceEditorPage />
                    </ProtectedAdminRoute>
                  }
                />
              </Routes>
              <Toaster
                position="bottom-center"
                richColors
                closeButton
                toastOptions={{ duration: 4000 }}
              />
            </div>
          </AuthProvider>
        </Router>
      </TooltipProvider>
    </ThemeProvider>
  );
}

export default App;
